import { ColumnFiltersState } from '@tanstack/react-table';
import { isEqual } from 'lodash';
import { createParser } from 'nuqs';

const OBJECT_SEPARATOR = ';';
const ARRAY_SEPARATOR = '|';
const KEY_VALUE_SEPARATOR = ':';

const serializeValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  if (Array.isArray(value)) {
    return value.map(serializeValue).join(ARRAY_SEPARATOR);
  }

  if (typeof value === 'object') {
    // Convert object to array of [key, serializedValue]
    return Object.entries(value)
      .filter(([, val]) => !!val)
      .map(([key, val]) => [key, serializeValue(val)])
      .map((entry) => entry.join(OBJECT_SEPARATOR))
      .join(ARRAY_SEPARATOR);
  }

  return encodeURIComponent(String(value));
};

const deserializeValue = (value: string, asArray: boolean = true): unknown => {
  if (value === 'null') return null;
  if (value === 'undefined') return undefined;

  if (/^-?\d+(\.\d+)?$/.test(value)) {
    return asArray ? [value] : Number(value);
  }

  if (value === 'true') return true;
  if (value === 'false') return false;

  if (value.includes(ARRAY_SEPARATOR) || value.includes(OBJECT_SEPARATOR)) {
    const items = value.split(ARRAY_SEPARATOR);
    // Check if this might be an object (each item contains ~)
    if (items.every((item) => item.includes(OBJECT_SEPARATOR))) {
      return items.reduce((obj, item) => {
        const [key, val] = item.split(OBJECT_SEPARATOR);
        return { ...obj, [key]: deserializeValue(val, false) };
      }, {});
    }
    // Regular array
    return items.map((val) => deserializeValue(val, false));
  }

  const decodedValue = decodeURIComponent(value);
  return asArray ? [decodedValue] : decodedValue;
};

export const parseAsColumnFilters = createParser<ColumnFiltersState>({
  parse(value) {
    if (!value) return [];

    return value.split(',').map((filterValue) => {
      const [id, selectedValuesRaw] = filterValue.split(KEY_VALUE_SEPARATOR);
      return {
        id,
        value: deserializeValue(selectedValuesRaw),
      };
    });
  },

  serialize(value) {
    return value.map((filter) => `${filter.id}${KEY_VALUE_SEPARATOR}${serializeValue(filter.value)}`).join(',');
  },

  eq: isEqual,
});
